import { ChevronDown, Clock3, Grid2X2, Image, Route, Sparkles } from "lucide-react";
import React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { formatDuration } from "../../utils";
import ProcessGraphLink from "../dashboard/ProcessGraphLink";
import SessionEvidence from "./SessionEvidence";
import SessionHighlights from "./SessionHighlights";
import SessionTimeline, { type SessionTimelineHandle } from "./SessionTimeline";
import { type EvidenceSelection, evidenceScreenshots, evidenceSteps } from "./sessionEvidenceModel";
import { sessionProcesses } from "./sessionStory";
import type { AnalyticsModel } from "./utils";

type OverviewTabProps = {
  model: AnalyticsModel;
  recordingId: string;
  canShowScreenshots: boolean;
  processSlugs?: Record<number, string>;
  selectedProcessSlug?: string | null;
  selectedInstanceNumber?: number | null;
  scrollToTimeline?: boolean;
  onTargetChange?: (slug: string | null, instance: number | null) => void;
  analysisReport?: React.ReactNode;
};
const OverviewTab = ({
  model,
  recordingId,
  canShowScreenshots,
  processSlugs = {},
  selectedProcessSlug,
  selectedInstanceNumber,
  scrollToTimeline,
  onTargetChange,
  analysisReport,
}: OverviewTabProps) => {
  const { t } = useTranslation();
  const groups = useMemo(() => sessionProcesses(model), [model]);
  const screenshots = useMemo(
    () =>
      evidenceScreenshots(evidenceSteps(model, { traceIndex: 0, eventIndex: 0, scope: "session" })),
    [model],
  );
  const [showAll, setShowAll] = useState(false);
  const [timelineZoomed, setTimelineZoomed] = useState(false);
  const [focusedProcessName, setFocusedProcessName] = useState<string | null>(null);
  const [selection, setSelection] = useState<(EvidenceSelection & { revision: number }) | null>(
    null,
  );
  const [activeStep, setActiveStep] = useState<{ traceIndex: number; eventIndex: number } | null>(
    null,
  );
  const timelineRef = useRef<HTMLDivElement>(null);
  const timelineHandle = useRef<SessionTimelineHandle>(null);
  const emittedTarget = useRef<string | null>(null);
  const publishTarget = (traceIndex: number | null, instance: boolean) => {
    const slug = traceIndex === null ? null : (processSlugs[traceIndex] ?? null);
    const group = groups.find(group => group.runs.some(run => run.index === traceIndex));
    const number =
      slug && instance && group ? group.runs.findIndex(run => run.index === traceIndex) + 1 : null;
    emittedTarget.current = JSON.stringify([slug, number]);
    onTargetChange?.(slug, number);
  };
  const focusProcess = (name: string) => {
    timelineHandle.current?.toggleProcess(name);
    timelineRef.current?.focus({ preventScroll: true });
    timelineRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const showEvidence = (next: EvidenceSelection, publish = true) => {
    if (publish)
      publishTarget(next.scope === "session" ? null : next.traceIndex, next.scope === "run");
    setActiveStep(null);
    setSelection(current => ({ ...next, revision: (current?.revision ?? 0) + 1 }));
  };
  const openEvidence = (name: string, index?: number, publish = true) => {
    const traceIndex = index ?? model.processes.findIndex(pv => pv.trace.name === name);
    if (traceIndex < 0) return;
    const next: EvidenceSelection = {
      traceIndex,
      eventIndex: 0,
      scope: index === undefined ? "process" : "run",
    };
    const firstStep = evidenceSteps(model, next)[0];
    showEvidence(
      firstStep
        ? { ...next, traceIndex: firstStep.traceIndex, eventIndex: firstStep.eventIndex }
        : next,
      publish,
    );
  };
  useEffect(() => {
    emittedTarget.current = null;
    setShowAll(false);
    setFocusedProcessName(null);
    setSelection(null);
    setActiveStep(null);
  }, [model, recordingId]);
  useEffect(() => {
    const target = JSON.stringify([selectedProcessSlug ?? null, selectedInstanceNumber ?? null]);
    if (emittedTarget.current === target) return;
    emittedTarget.current = null;
    const group =
      selectedProcessSlug &&
      groups.find(g => g.runs.some(run => processSlugs[run.index] === selectedProcessSlug));
    setFocusedProcessName(group ? group.name : null);
    timelineHandle.current?.restoreProcess(group ? group.name : null);
    if (group) {
      openEvidence(
        group.name,
        selectedInstanceNumber ? group.runs[selectedInstanceNumber - 1]?.index : undefined,
        false,
      );
    } else {
      setSelection(null);
      setActiveStep(null);
    }
    if (!group && scrollToTimeline)
      requestAnimationFrame(() => timelineRef.current?.scrollIntoView({ behavior: "smooth" }));
  }, [groups, processSlugs, selectedProcessSlug, selectedInstanceNumber, scrollToTimeline]);

  return (
    <div className="space-y-5">
      <section
        className="rounded-xl border border-outline bg-surface p-5 @[1100px]:p-6"
        aria-label={t("sessionStory.summary")}
      >
        <div className="grid gap-6 @[1000px]:grid-cols-[minmax(0,1.5fr)_minmax(360px,1fr)]">
          <div>
            <h2 className="flex items-center gap-2.5 text-base font-semibold text-text">
              <Sparkles size={19} className="text-violet-500" />
              {t("sessionStory.summary")}
            </h2>
            {model.sessionSummary ? (
              <p className="mt-3 max-w-3xl text-sm leading-6 text-text-mid">
                {model.sessionSummary}
              </p>
            ) : (
              <>
                <span className="mt-3 inline-block rounded bg-muted-surface px-2 py-0.5 text-xs font-medium text-text-dim">
                  {t("sessionStory.placeholder")}
                </span>
                <p className="mt-2 max-w-3xl text-sm leading-6 text-text-mid">
                  {t("sessionStory.summaryPlaceholder")}
                </p>
              </>
            )}
          </div>
          <dl className="grid grid-cols-3 items-center gap-2 border-t border-outline pt-5 @[1000px]:border-l @[1000px]:border-t-0 @[1000px]:pl-6 @[1000px]:pt-0">
            {[
              {
                icon: Clock3,
                value: formatDuration(model.totalActive * 60),
                label: t("sessionStory.mappedTime"),
                color: "text-cyan-600 bg-cyan-500/10",
              },
              {
                icon: Route,
                value: groups.length,
                label: t("sessionStory.processes"),
                color: "text-blue-600 bg-blue-500/10",
              },
              {
                icon: Grid2X2,
                value: model.appUsage.length || "—",
                label: t("sessionStory.apps"),
                color: "text-indigo-500 bg-indigo-500/10",
              },
            ].map(({ icon: Icon, value, label, color }) => (
              <div key={label} className="min-w-0">
                <dd className="flex items-center gap-1.5 text-lg font-semibold tabular-nums text-text @[600px]:text-2xl">
                  <Icon size={24} className={`shrink-0 rounded-md p-1 ${color}`} />
                  <span>{value}</span>
                </dd>
                <dt className="mt-0.5 text-xs text-text-dim">{label}</dt>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {analysisReport}

      <section
        ref={timelineRef}
        tabIndex={-1}
        aria-label={t("sessionStory.whatHappened")}
        className="scroll-mt-20 rounded-xl border border-outline bg-surface p-5 @[1100px]:p-6"
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-text">{t("sessionStory.whatHappened")}</h2>
            <p className="mt-1 text-sm text-text-dim">{t("sessionStory.timelineDescription")}</p>
          </div>
          <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => timelineHandle.current?.resetZoom()}
              disabled={!timelineZoomed}
              aria-hidden={!timelineZoomed}
              className={`rounded-lg border border-outline px-3 py-2 text-sm font-medium text-text-mid hover:bg-muted-surface focus-visible:outline-info ${timelineZoomed ? "" : "invisible"}`}
            >
              {t("sessionStory.showFullSession")}
            </button>
            {canShowScreenshots && (
              <button
                type="button"
                onClick={() =>
                  showEvidence({
                    traceIndex: screenshots[0]?.traceIndex ?? 0,
                    eventIndex: screenshots[0]?.eventIndex ?? 0,
                    scope: "session",
                  })
                }
                disabled={screenshots.length === 0}
                title={screenshots.length === 0 ? t("sessionStory.noScreenshots") : undefined}
                className="inline-flex items-center gap-2 rounded-lg border border-outline bg-surface px-3 py-2 text-sm font-medium text-text hover:bg-muted-surface disabled:cursor-default disabled:opacity-50"
              >
                <Image size={16} />
                {t("sessionStory.browseScreenshots")}
              </button>
            )}
          </div>
        </div>
        <div className={focusedProcessName ? "mt-3" : "mt-5"}>
          <SessionTimeline
            ref={timelineHandle}
            model={model}
            selectedStep={activeStep}
            onZoomChange={setTimelineZoomed}
            onFocusProcess={name => {
              setFocusedProcessName(name);
              if (name && groups.findIndex(group => group.name === name) >= 5) setShowAll(true);
              if (selection && name) openEvidence(name);
              else if (!name && selection)
                publishTarget(
                  selection.scope === "session" ? null : selection.traceIndex,
                  selection.scope === "run",
                );
              else
                publishTarget(
                  name ? (groups.find(group => group.name === name)?.runs[0]?.index ?? null) : null,
                  false,
                );
            }}
            onSelectRun={traceIndex => showEvidence({ traceIndex, eventIndex: 0, scope: "run" })}
            onSelectEvent={event =>
              showEvidence({
                traceIndex: event.traceIndex,
                eventIndex: event.eventIndex ?? 0,
                scope: focusedProcessName ? "run" : "process",
              })
            }
          />
          {selection && (
            <SessionEvidence
              key={selection.revision}
              model={model}
              recordingId={recordingId}
              canShowScreenshots={canShowScreenshots}
              selection={selection}
              onActiveStep={setActiveStep}
              onClose={() => {
                publishTarget(
                  focusedProcessName
                    ? (groups.find(group => group.name === focusedProcessName)?.runs[0]?.index ??
                        null)
                    : null,
                  false,
                );
                setSelection(null);
                setActiveStep(null);
              }}
            />
          )}
        </div>
      </section>

      <div className="grid items-start gap-5 @[1100px]:grid-cols-2">
        <section className="rounded-xl border border-outline bg-surface p-5 @[1100px]:p-6">
          <h2 className="text-base font-semibold text-text">{t("sessionStory.processList")}</h2>
          <p className="mt-1 text-xs text-text-dim">
            {t("sessionStory.detected", { count: groups.length })}
          </p>
          <ul className="mt-3 divide-y divide-outline">
            {(showAll ? groups : groups.slice(0, 5)).map(group => {
              const slug = group.runs.map(run => processSlugs[run.index]).find(Boolean);
              const content = (
                <>
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ background: group.color }}
                  />
                  <span className="min-w-0 flex-1 @[600px]:contents">
                    <span className="block min-w-0 flex-1 text-sm font-medium text-text">
                      {group.name}
                    </span>
                    <span className="mt-1 flex gap-3 @[600px]:contents">
                      <span className="shrink-0 text-xs tabular-nums text-text-mid">
                        {formatDuration(group.totalMin * 60)}
                      </span>
                      <span className="shrink-0 text-xs text-text-dim @[600px]:w-20 @[600px]:text-right">
                        {t("sessionStory.runCount", {
                          count: group.runs.length,
                        })}
                      </span>
                    </span>
                  </span>
                </>
              );
              const isSelected = focusedProcessName === group.name;
              const classes = `flex min-w-0 flex-1 items-center gap-3 rounded-md px-3 py-3 text-left transition focus-visible:outline-info ${
                isSelected
                  ? "bg-info/10 ring-1 ring-inset ring-info/30 hover:bg-info/15"
                  : "hover:bg-muted-surface"
              }`;
              return (
                <li key={group.name} className="flex items-center gap-3">
                  <button
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() => focusProcess(group.name)}
                    className={classes}
                  >
                    {content}
                  </button>
                  {slug && <ProcessGraphLink slug={slug} name={group.name} />}
                </li>
              );
            })}
          </ul>
          {groups.length === 0 && (
            <p className="mt-4 text-sm text-text-dim">{t("no_meaningful_process_was_found")}</p>
          )}
          {groups.length > 5 && (
            <button
              type="button"
              aria-expanded={showAll}
              onClick={() => setShowAll(!showAll)}
              className="inline-flex items-center gap-2 self-start pt-5 text-sm font-medium text-info hover:underline focus-visible:outline-info"
            >
              {showAll
                ? t("sessionStory.showLess")
                : t("sessionStory.showAllProcesses", { count: groups.length })}
              <ChevronDown size={16} aria-hidden="true" className={showAll ? "rotate-180" : ""} />
            </button>
          )}
        </section>
        <SessionHighlights model={model} groups={groups} />
      </div>
    </div>
  );
};

export default OverviewTab;
